import { Badge, Button, SimplePagination } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, Eye, HelpCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { SimpleSelectOption } from "@/shared/ui/SimpleSelect";
import { useStandardListQuery } from "../api/queries";
import { labelServiceStage } from "../lib/trainingLaoLabels";
import {
  sliceTrainingListPage,
  TRAINING_LIST_PAGE_SIZE,
  trainingListTotalPages,
} from "../lib/trainingListPagination";
import { TrainingTour } from "../tour/TrainingTour";
import { serviceStandardListTourSteps } from "../tour/trainingTourSteps";
import { useTrainingTour } from "../tour/useTrainingTour";
import { trainingRoutes } from "../trainingRoutes";
import { TrainingListControls } from "../ui/TrainingListControls";
import { TrainingResourceCardGridSkeleton } from "../ui/TrainingResourceCardGridSkeleton";

const STANDARD_SORT_OPTIONS: SimpleSelectOption[] = [
  { value: "name", label: "ຊື່ມາດຕະຖານ" },
  { value: "serviceStage", label: "ຊ່ວງບໍລິການ" },
  { value: "description", label: "ລາຍລະອຽດ" },
];

const STANDARD_SORT_IDS = new Set(STANDARD_SORT_OPTIONS.map((o) => o.value));

export function ServiceStandardListPage() {
  const tour = useTrainingTour("training-standard-list-tour-completed");
  const navigate = useNavigate({ from: "/app/training/standards" });
  const routeSearch = useSearch({ from: "/app/training/standards" });
  const rawSortBy = routeSearch.sortBy ?? "name";
  const sortBy = STANDARD_SORT_IDS.has(rawSortBy) ? rawSortBy : "name";
  const sortOrder: "asc" | "desc" =
    routeSearch.sortOrder === "desc" ? "desc" : "asc";
  const { data, isLoading } = useStandardListQuery(sortBy, sortOrder);
  const rows = (data?.data ?? []) as Array<{
    id: string;
    name: string;
    description: string | null;
    serviceStage: string;
  }>;
  const [filterQuery, setFilterQuery] = useState("");
  const filteredRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const name = row.name.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      const stage = labelServiceStage(row.serviceStage).toLowerCase();
      return name.includes(q) || description.includes(q) || stage.includes(q);
    });
  }, [rows, filterQuery]);

  const [page, setPage] = useState(1);
  const totalPages = trainingListTotalPages(filteredRows.length);
  const safePage = Math.min(page, totalPages);
  const listResetKey = `${filterQuery}\0${sortBy}\0${sortOrder}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: listResetKey encodes filter + sort; reset page when it changes
  useEffect(() => {
    setPage(1);
  }, [listResetKey]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(
    () => sliceTrainingListPage(filteredRows, safePage),
    [filteredRows, safePage],
  );

  const emptyMessage =
    rows.length === 0 ? "ບໍ່ມີມາດຕະຖານທີ່ກ່ຽວຂ້ອງ" : "ບໍ່ພົບມາດຕະຖານທີ່ກົງກັບການຄົ້ນຫາ";

  return (
    <>
      <Header />
      <Main>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h1 className="font-bold text-2xl sm:text-3xl">ມາດຕະຖານບໍລິການ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:mb-4 sm:w-auto"
            onClick={() =>
              navigate({
                from: "/app/training/standards",
                to: trainingRoutes.exitToAppHome,
              })
            }
          >
            <ArrowLeftIcon className="h-4 w-4" /> ກັບຄືນ
          </Button>
        </div>

        <TrainingListControls
          filterInputId="standard-list-search"
          filterLabel="ຄົ້ນຫາມາດຕະຖານບໍລິການ"
          filterQuery={filterQuery}
          onFilterChange={setFilterQuery}
          searchPlaceholder="ຄົ້ນຫາມາດຕະຖານ..."
          filteredCount={filteredRows.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          sortOptions={STANDARD_SORT_OPTIONS}
          onSortByChange={(next) =>
            navigate({
              search: {
                ...routeSearch,
                sortBy: next,
              },
            })
          }
          onSortOrderToggle={() =>
            navigate({
              search: {
                ...routeSearch,
                sortOrder: sortOrder === "asc" ? "desc" : "asc",
              },
            })
          }
          searchTourId="training-standard-list-search"
        />

        <div
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
          data-tourid="training-standard-list-grid"
        >
          {isLoading ? (
            <TrainingResourceCardGridSkeleton />
          ) : filteredRows.length === 0 ? (
            <p className="col-span-full py-14 text-center text-muted-foreground text-sm">
              {emptyMessage}
            </p>
          ) : (
            pageItems.map((row) => (
              <button
                key={row.id}
                type="button"
                className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-colors hover:border-primary/30 hover:bg-muted/25"
                onClick={() =>
                  navigate({
                    from: "/app/training/standards",
                    to: trainingRoutes.standardDetail,
                    params: { id: row.id },
                  })
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-semibold text-base leading-snug group-hover:text-primary">
                    {row.name}
                  </h2>
                  <Eye
                    className="size-4 shrink-0 text-muted-foreground group-hover:text-primary"
                    aria-hidden
                  />
                </div>
                <div className="mt-2">
                  <Badge variant="outline" className="font-normal text-xs">
                    {labelServiceStage(row.serviceStage)}
                  </Badge>
                </div>
                <p className="mt-3 line-clamp-3 text-muted-foreground text-sm leading-relaxed">
                  {row.description?.trim() || "—"}
                </p>
              </button>
            ))
          )}
        </div>

        {!isLoading && filteredRows.length > 0 && totalPages > 1 ? (
          <div className="mt-8 flex justify-center">
            <SimplePagination
              page={safePage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        ) : null}

        {!isLoading && filteredRows.length > 0 ? (
          <p className="mt-4 text-center text-muted-foreground text-xs">
            {totalPages > 1
              ? `ໜ້າ ${safePage} ຈາກ ${totalPages} · ສູງສຸດ ${TRAINING_LIST_PAGE_SIZE} ລາຍການຕໍ່ໜ້າ`
              : `${filteredRows.length} ລາຍການ`}
          </p>
        ) : null}

        <TrainingTour
          steps={serviceStandardListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
